import type {
    MapPath,
    MissionPointDraft,
    MissionPointItem,
    MissionRouteConfig,
    MissionRouteMode,
} from './missionModels';

type RouteControlPoint = {
    lat?: number | null;
    lng?: number | null;
    source?: 'pattern' | 'custom';
    patternPointId?: number | null;
    params?: Record<string, any> | null;
    direction?: XYPoint | null;
    directionMode?: 'pattern' | 'auto' | 'manual' | null;
};

type LocalRouteControlPoint = Omit<RouteControlPoint, 'lat' | 'lng'> & {
    x?: number | null;
    y?: number | null;
};

type XYPoint = {
    x: number;
    y: number;
};

type ActualRouteNode = {
    kind: 'actual';
    actualIndex: number;
    point: XYPoint;
    source?: 'pattern' | 'custom';
    patternPointId?: number | null;
    params?: Record<string, any> | null;
    direction?: XYPoint | null;
};

type VirtualRouteNode = {
    kind: 'virtual';
    actualIndex: null;
    point: XYPoint;
    warning: boolean;
};

type RouteNode = ActualRouteNode | VirtualRouteNode;

type DetectedCorridor = {
    startLocalIndex: number;
    endLocalIndex: number;
    direction: XYPoint;
    lengthMeters: number;
    pointCount: number;
};

type PointRoutePlan = {
    backwardDirection: XYPoint | null;
    forwardDirection: XYPoint | null;
    backwardOffsetMeters: number;
    forwardOffsetMeters: number;
    backwardWarning: boolean;
    forwardWarning: boolean;
    backwardManual: boolean;
    forwardManual: boolean;
};

type CornerSpec = {
    entryPoint: XYPoint;
    exitPoint: XYPoint;
    center: XYPoint;
    radiusMeters: number;
    turnOrientation: 1 | -1;
};

type PreviewLegPath = {
    id: string;
    coordinates: Array<{ lat: number; lng: number }>;
    warning?: boolean;
};

type PointDirectionMode = 'pattern' | 'auto' | 'manual';

const DEFAULT_TURN_RADIUS_METERS = 3;
const MIN_SAMPLE_STEP_METERS = 0.25;
const MAX_SAMPLE_STEP_METERS = 5;
const MIN_TURN_ANGLE_RADIANS = (5 * Math.PI) / 180;
const MAX_TURN_ANGLE_RADIANS = Math.PI - MIN_TURN_ANGLE_RADIANS;
const CORRIDOR_ALIGNMENT_TOLERANCE_RADIANS = (6 * Math.PI) / 180;
const MIN_VIRTUAL_OFFSET_METERS = 0.05;
const EPSILON = 1e-6;

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

const roundNumber = (value: number, decimals = 2) => Number(value.toFixed(decimals));

const normalizeAngle = (angle: number) => {
    let normalized = angle;
    while (normalized <= -Math.PI) normalized += Math.PI * 2;
    while (normalized > Math.PI) normalized -= Math.PI * 2;
    return normalized;
};

const buildDefaultSampleStep = (turnRadiusMeters: number) => (
    turnRadiusMeters > 0
        ? clamp(turnRadiusMeters / 4, 0.75, 2.5)
        : 2
);

export const normalizeMissionRouteConfig = (value?: Partial<MissionRouteConfig> | null): MissionRouteConfig => {
    const candidate = value && typeof value === 'object' ? value : {};
    const turnRadiusMeters = clamp(
        Number.isFinite(Number(candidate.turnRadiusMeters))
            ? Number(candidate.turnRadiusMeters)
            : DEFAULT_TURN_RADIUS_METERS,
        0,
        100
    );
    const sampleStepMeters = clamp(
        Number.isFinite(Number(candidate.sampleStepMeters))
            ? Number(candidate.sampleStepMeters)
            : buildDefaultSampleStep(turnRadiusMeters),
        MIN_SAMPLE_STEP_METERS,
        MAX_SAMPLE_STEP_METERS
    );
    const rawMode = String(candidate.routingMode || '').trim().toLowerCase();
    const routingMode: MissionRouteMode = (
        turnRadiusMeters <= 0.05 || rawMode === 'direct'
            ? 'direct'
            : rawMode === 'curve'
                ? 'curve'
                : 'dubins'
    );

    const patternGuidedDirections = candidate.patternGuidedDirections !== false;

    return {
        turnRadiusMeters: roundNumber(turnRadiusMeters, 2),
        sampleStepMeters: roundNumber(sampleStepMeters, 2),
        routingMode,
        patternGuidedDirections,
    };
};

const isValidCoordinate = (value: number | null | undefined) => Number.isFinite(Number(value));

const isValidPoint = (point?: RouteControlPoint | null): point is Required<RouteControlPoint> => Boolean(
    point
    && isValidCoordinate(point.lat)
    && isValidCoordinate(point.lng)
);

const isValidLocalRoutePoint = (point?: LocalRouteControlPoint | null): point is Required<LocalRouteControlPoint> => Boolean(
    point
    && isValidCoordinate(point.x)
    && isValidCoordinate(point.y)
);

const subtract = (a: XYPoint, b: XYPoint): XYPoint => ({
    x: a.x - b.x,
    y: a.y - b.y,
});

const add = (a: XYPoint, b: XYPoint): XYPoint => ({
    x: a.x + b.x,
    y: a.y + b.y,
});

const scale = (vector: XYPoint, factor: number): XYPoint => ({
    x: vector.x * factor,
    y: vector.y * factor,
});

const magnitude = (vector: XYPoint) => Math.sqrt(vector.x ** 2 + vector.y ** 2);

const distanceBetweenXY = (a: XYPoint, b: XYPoint) => magnitude(subtract(a, b));

const normalizeVector = (vector: XYPoint): XYPoint | null => {
    const length = magnitude(vector);
    if (length < EPSILON) {
        return null;
    }

    return {
        x: vector.x / length,
        y: vector.y / length,
    };
};

const dot = (a: XYPoint, b: XYPoint) => (a.x * b.x) + (a.y * b.y);

const cross = (a: XYPoint, b: XYPoint) => (a.x * b.y) - (a.y * b.x);

const leftNormal = (vector: XYPoint): XYPoint => ({
    x: -vector.y,
    y: vector.x,
});

const getTurnGeometry = (
    incomingDirection: XYPoint,
    outgoingDirection: XYPoint,
    radiusMeters: number
) => {
    const turnAngle = Math.acos(clamp(dot(incomingDirection, outgoingDirection), -1, 1));
    if (turnAngle < MIN_TURN_ANGLE_RADIANS || turnAngle > MAX_TURN_ANGLE_RADIANS) {
        return null;
    }

    const turnCross = cross(incomingDirection, outgoingDirection);
    if (Math.abs(turnCross) < EPSILON) {
        return null;
    }

    const tangentFactor = Math.tan(turnAngle / 2);
    if (!Number.isFinite(tangentFactor) || tangentFactor < EPSILON) {
        return null;
    }

    return {
        turnOrientation: turnCross > 0 ? 1 as const : -1 as const,
        trimDistance: radiusMeters * tangentFactor,
    };
};

const buildProjection = (points: RouteControlPoint[]) => {
    const validPoints = points.filter(isValidPoint);
    const referenceLat = validPoints.reduce((sum, point) => sum + Number(point.lat), 0) / validPoints.length;
    const referenceLng = validPoints.reduce((sum, point) => sum + Number(point.lng), 0) / validPoints.length;
    const metersPerDegLat = 111132.92;
    const metersPerDegLng = 111319.49 * Math.cos((referenceLat * Math.PI) / 180);

    return {
        toXY: (point: Required<RouteControlPoint>): XYPoint => ({
            x: (Number(point.lng) - referenceLng) * metersPerDegLng,
            y: (Number(point.lat) - referenceLat) * metersPerDegLat,
        }),
        fromXY: (point: XYPoint) => ({
            lat: point.y / metersPerDegLat + referenceLat,
            lng: point.x / metersPerDegLng + referenceLng,
        }),
    };
};

const normalizeDirectionMode = (value: unknown): PointDirectionMode | null => {
    const normalized = String(value || '').trim().toLowerCase();
    if (normalized === 'pattern' || normalized === 'auto' || normalized === 'manual') {
        return normalized;
    }
    return null;
};

const extractStoredDirection = (point?: RouteControlPoint | null): XYPoint | null => {
    const candidate = point?.direction
        || point?.params?.pathDirection
        || null;

    if (!candidate || typeof candidate !== 'object') {
        return null;
    }

    const x = Number((candidate as any).x);
    const y = Number((candidate as any).y);
    if (!Number.isFinite(x) || !Number.isFinite(y)) {
        return null;
    }

    return normalizeVector({ x, y });
};

const buildPointParamsWithDirection = (
    params: Record<string, any> | null | undefined,
    direction: XYPoint | null,
    mode: PointDirectionMode | null
) => {
    const nextParams = {
        ...(params || {}),
    };

    if (direction) {
        nextParams.pathDirection = {
            x: roundNumber(direction.x, 6),
            y: roundNumber(direction.y, 6),
        };
    } else {
        delete nextParams.pathDirection;
    }

    if (mode) {
        nextParams.pathDirectionMode = mode;
    } else {
        delete nextParams.pathDirectionMode;
    }

    return nextParams;
};

const extractManualLeadDistance = (
    params: Record<string, any> | null | undefined,
    key: 'pathLeadInMeters' | 'pathLeadOutMeters'
) => {
    const value = Number(params?.[key]);
    if (!Number.isFinite(value) || value < 0) {
        return null;
    }

    return clamp(value, 0, 100);
};

const pushUniquePoint = (points: XYPoint[], point: XYPoint) => {
    const lastPoint = points[points.length - 1];
    if (!lastPoint || distanceBetweenXY(lastPoint, point) > 0.001) {
        points.push(point);
        return;
    }

    points[points.length - 1] = point;
};

const appendPolyline = (base: XYPoint[], pointsToAdd: XYPoint[]) => {
    pointsToAdd.forEach((point) => {
        pushUniquePoint(base, point);
    });
};

const sampleStraightSegment = (start: XYPoint, end: XYPoint, sampleStepMeters: number): XYPoint[] => {
    const segmentLength = distanceBetweenXY(start, end);
    if (segmentLength < EPSILON) {
        return [start];
    }

    const steps = Math.max(1, Math.ceil(segmentLength / Math.max(sampleStepMeters, MIN_SAMPLE_STEP_METERS)));
    const points: XYPoint[] = [];

    for (let index = 0; index <= steps; index += 1) {
        const t = index / steps;
        points.push({
            x: start.x + ((end.x - start.x) * t),
            y: start.y + ((end.y - start.y) * t),
        });
    }

    return points;
};

const computeArcDelta = (
    center: XYPoint,
    start: XYPoint,
    end: XYPoint,
    turnOrientation: 1 | -1
) => {
    const startAngle = Math.atan2(start.y - center.y, start.x - center.x);
    const endAngle = Math.atan2(end.y - center.y, end.x - center.x);
    let deltaAngle = normalizeAngle(endAngle - startAngle);

    if (turnOrientation > 0 && deltaAngle < 0) {
        deltaAngle += Math.PI * 2;
    } else if (turnOrientation < 0 && deltaAngle > 0) {
        deltaAngle -= Math.PI * 2;
    }

    return deltaAngle;
};

const sampleArc = (
    center: XYPoint,
    start: XYPoint,
    end: XYPoint,
    radiusMeters: number,
    turnOrientation: 1 | -1,
    sampleStepMeters: number
): XYPoint[] => {
    const deltaAngle = computeArcDelta(center, start, end, turnOrientation);
    const arcLengthMeters = Math.abs(deltaAngle) * radiusMeters;
    const steps = Math.max(2, Math.ceil(arcLengthMeters / Math.max(sampleStepMeters, MIN_SAMPLE_STEP_METERS)));
    const startAngle = Math.atan2(start.y - center.y, start.x - center.x);
    const points: XYPoint[] = [];

    for (let index = 0; index <= steps; index += 1) {
        const t = index / steps;
        const angle = startAngle + (deltaAngle * t);
        points.push({
            x: center.x + Math.cos(angle) * radiusMeters,
            y: center.y + Math.sin(angle) * radiusMeters,
        });
    }

    return points;
};

const intersectLines = (
    pointA: XYPoint,
    directionA: XYPoint,
    pointB: XYPoint,
    directionB: XYPoint
): XYPoint | null => {
    const denominator = cross(directionA, directionB);
    if (Math.abs(denominator) < EPSILON) {
        return null;
    }

    const delta = subtract(pointB, pointA);
    const factorA = cross(delta, directionB) / denominator;
    return add(pointA, scale(directionA, factorA));
};

const isStraightContinuation = (
    previousPoint: XYPoint,
    currentPoint: XYPoint,
    nextPoint: XYPoint
) => {
    const incomingDirection = normalizeVector(subtract(currentPoint, previousPoint));
    const outgoingDirection = normalizeVector(subtract(nextPoint, currentPoint));

    if (!incomingDirection || !outgoingDirection) {
        return false;
    }

    return dot(incomingDirection, outgoingDirection) >= Math.cos(CORRIDOR_ALIGNMENT_TOLERANCE_RADIANS)
        && Math.abs(cross(incomingDirection, outgoingDirection)) <= Math.sin(CORRIDOR_ALIGNMENT_TOLERANCE_RADIANS);
};

const findSharedOffsetForCorridorTransition = (
    exitAnchorPoint: XYPoint,
    exitDirection: XYPoint,
    entryAnchorPoint: XYPoint,
    entryDirection: XYPoint,
    routeConfig: MissionRouteConfig
) => {
    const normalizedExitDirection = normalizeVector(exitDirection);
    const normalizedEntryDirection = normalizeVector(entryDirection);
    if (
        !normalizedExitDirection
        || !normalizedEntryDirection
        || routeConfig.routingMode === 'direct'
        || routeConfig.turnRadiusMeters <= 0.05
    ) {
        return { offsetMeters: 0, warning: false };
    }

    const buildTransitionCorners = (offsetMeters: number) => {
        const exitVirtualPoint = add(exitAnchorPoint, scale(normalizedExitDirection, offsetMeters));
        const entryVirtualPoint = add(entryAnchorPoint, scale(normalizedEntryDirection, offsetMeters));
        const exitCorner = buildCornerSpec(exitAnchorPoint, exitVirtualPoint, entryVirtualPoint, routeConfig);
        const entryCorner = buildCornerSpec(exitVirtualPoint, entryVirtualPoint, entryAnchorPoint, routeConfig);

        if (!exitCorner || !entryCorner) {
            return null;
        }

        return {
            exitCorner,
            entryCorner,
        };
    };

    let lowerBound = 0;
    let upperBound = Math.max(routeConfig.turnRadiusMeters, 0.5);
    const maxSearchDistance = Math.max(
        routeConfig.turnRadiusMeters * 20,
        distanceBetweenXY(exitAnchorPoint, entryAnchorPoint) + (routeConfig.turnRadiusMeters * 4),
        1
    );

    while (upperBound <= maxSearchDistance && !buildTransitionCorners(upperBound)) {
        lowerBound = upperBound;
        upperBound *= 2;
    }

    if (!buildTransitionCorners(upperBound)) {
        return {
            offsetMeters: 0,
            warning: true,
        };
    }

    for (let iteration = 0; iteration < 24; iteration += 1) {
        const midpoint = (lowerBound + upperBound) / 2;
        if (buildTransitionCorners(midpoint)) {
            upperBound = midpoint;
        } else {
            lowerBound = midpoint;
        }
    }

    return {
        offsetMeters: upperBound,
        warning: false,
    };
};

const buildDetectedCorridorsForBlock = (block: ActualRouteNode[]): DetectedCorridor[] => {
    if (block.length < 3) {
        return [];
    }

    const corridors: DetectedCorridor[] = [];

    for (let middleIndex = 1; middleIndex < block.length - 1; middleIndex += 1) {
        if (!isStraightContinuation(block[middleIndex - 1].point, block[middleIndex].point, block[middleIndex + 1].point)) {
            continue;
        }

        const startLocalIndex = middleIndex - 1;
        let corridorEndMiddleIndex = middleIndex;

        while (
            corridorEndMiddleIndex + 1 < block.length - 1
            && isStraightContinuation(
                block[corridorEndMiddleIndex].point,
                block[corridorEndMiddleIndex + 1].point,
                block[corridorEndMiddleIndex + 2].point
            )
        ) {
            corridorEndMiddleIndex += 1;
        }

        const endLocalIndex = corridorEndMiddleIndex + 1;
        const direction = normalizeVector(subtract(block[endLocalIndex].point, block[startLocalIndex].point))
            || normalizeVector(subtract(block[startLocalIndex + 1].point, block[startLocalIndex].point));

        if (direction) {
            corridors.push({
                startLocalIndex,
                endLocalIndex,
                direction,
                lengthMeters: distanceBetweenXY(block[startLocalIndex].point, block[endLocalIndex].point),
                pointCount: endLocalIndex - startLocalIndex + 1,
            });
        }

        middleIndex = corridorEndMiddleIndex;
    }

    return corridors;
};

const compareCorridorStrength = (
    candidate: DetectedCorridor,
    current: DetectedCorridor | null
) => {
    if (!current) {
        return 1;
    }

    if (Math.abs(candidate.lengthMeters - current.lengthMeters) > EPSILON) {
        return candidate.lengthMeters - current.lengthMeters;
    }

    if (candidate.pointCount !== current.pointCount) {
        return candidate.pointCount - current.pointCount;
    }

    return current.startLocalIndex - candidate.startLocalIndex;
};

const buildValidPatternBlocks = (
    points: RouteControlPoint[],
    projectedPoints: Array<XYPoint | null>
): ActualRouteNode[][] => {
    const blocks: ActualRouteNode[][] = [];
    let currentBlock: ActualRouteNode[] = [];

    projectedPoints.forEach((point, actualIndex) => {
        const sourcePoint = points[actualIndex];
        const isPatternPoint = Boolean(
            point
            && (sourcePoint?.patternPointId || sourcePoint?.source === 'pattern')
        );

        if (point && isPatternPoint) {
            currentBlock.push({
                kind: 'actual',
                actualIndex,
                point,
                source: sourcePoint?.source,
                patternPointId: sourcePoint?.patternPointId ?? null,
                params: sourcePoint?.params || null,
                direction: null,
            });
            return;
        }

        if (currentBlock.length > 0) {
            blocks.push(currentBlock);
            currentBlock = [];
        }
    });

    if (currentBlock.length > 0) {
        blocks.push(currentBlock);
    }

    return blocks;
};

const resolvePatternDirections = (
    points: RouteControlPoint[],
    projectedPoints: Array<XYPoint | null>
) => {
    const directionByIndex = new Map<number, XYPoint>();
    const blocks = buildValidPatternBlocks(points, projectedPoints);

    blocks.forEach((block) => {
        const detectedCorridors = buildDetectedCorridorsForBlock(block);
        const dominantCorridorByIndex: Array<DetectedCorridor | null> = Array.from({ length: block.length }, () => null);

        detectedCorridors.forEach((corridor) => {
            for (let localIndex = corridor.startLocalIndex; localIndex <= corridor.endLocalIndex; localIndex += 1) {
                if (compareCorridorStrength(corridor, dominantCorridorByIndex[localIndex]) > 0) {
                    dominantCorridorByIndex[localIndex] = corridor;
                }
            }
        });

        block.forEach((node, localIndex) => {
            const previousPoint = localIndex > 0 ? block[localIndex - 1].point : null;
            const nextPoint = localIndex < block.length - 1 ? block[localIndex + 1].point : null;
            const direction = dominantCorridorByIndex[localIndex]?.direction
                || (previousPoint ? normalizeVector(subtract(node.point, previousPoint)) : null)
                || (nextPoint ? normalizeVector(subtract(nextPoint, node.point)) : null);

            if (direction) {
                directionByIndex.set(node.actualIndex, direction);
            }
        });
    });

    return directionByIndex;
};

const deriveAutoDirection = (
    projectedPoints: Array<XYPoint | null>,
    actualIndex: number
) => {
    const currentPoint = projectedPoints[actualIndex];
    if (!currentPoint) {
        return null;
    }

    let incomingDirection: XYPoint | null = null;
    for (let previousIndex = actualIndex - 1; previousIndex >= 0; previousIndex -= 1) {
        const previousPoint = projectedPoints[previousIndex];
        if (previousPoint) {
            incomingDirection = normalizeVector(subtract(currentPoint, previousPoint));
            if (incomingDirection) {
                break;
            }
        }
    }

    let outgoingDirection: XYPoint | null = null;
    for (let nextIndex = actualIndex + 1; nextIndex < projectedPoints.length; nextIndex += 1) {
        const nextPoint = projectedPoints[nextIndex];
        if (nextPoint) {
            outgoingDirection = normalizeVector(subtract(nextPoint, currentPoint));
            if (outgoingDirection) {
                break;
            }
        }
    }

    if (incomingDirection && outgoingDirection) {
        // Use the path tangent at an intermediate waypoint. Adding unit
        // vectors performs a circular mean, so headings around 0/360 degrees
        // average correctly and unequal segment lengths do not bias the result.
        // A 180-degree reversal has no defined bisector; face the outgoing leg.
        return normalizeVector(add(incomingDirection, outgoingDirection)) || outgoingDirection;
    }

    // The first point faces the next point; the last keeps the arrival heading.
    return outgoingDirection || incomingDirection;
};

const resolveRouteControlPointDirections = (
    points: RouteControlPoint[]
): RouteControlPoint[] => {
    const validPoints = points.filter(isValidPoint);
    if (validPoints.length === 0) {
        return points;
    }

    const projection = buildProjection(points);
    const projectedPoints = points.map((point) => (
        isValidPoint(point)
            ? projection.toXY(point)
            : null
    ));
    const patternDirectionsByIndex = resolvePatternDirections(points, projectedPoints);

    return points.map((point, actualIndex) => {
        const storedDirection = extractStoredDirection(point);
        const storedMode = normalizeDirectionMode(point?.directionMode || point?.params?.pathDirectionMode);
        const isPatternPoint = Boolean(point.patternPointId || point.source === 'pattern');

        let direction: XYPoint | null = null;
        let directionMode: PointDirectionMode | null = null;

        if (storedMode === 'manual' && storedDirection) {
            direction = storedDirection;
            directionMode = 'manual';
        } else if (isPatternPoint && storedMode !== 'auto') {
            direction = patternDirectionsByIndex.get(actualIndex) || storedDirection || deriveAutoDirection(projectedPoints, actualIndex);
            directionMode = direction ? 'pattern' : null;
        } else {
            // Automatic directions must follow the current geometry. A prior
            // save may contain an auto-generated pathDirection; treating it as
            // authoritative would lock the old heading after points are moved.
            direction = deriveAutoDirection(projectedPoints, actualIndex);
            directionMode = direction ? 'auto' : null;
        }

        return {
            ...point,
            direction,
            directionMode,
            params: buildPointParamsWithDirection(point.params, direction, directionMode),
        };
    });
};

const resolveLocalRouteControlPointDirections = (
    points: LocalRouteControlPoint[]
): LocalRouteControlPoint[] => {
    const validPoints = points.filter(isValidLocalRoutePoint);
    if (validPoints.length === 0) {
        return points;
    }

    const projectedPoints = points.map((point) => (
        isValidLocalRoutePoint(point)
            ? { x: Number(point.x), y: Number(point.y) }
            : null
    ));
    const patternDirectionsByIndex = resolvePatternDirections(points, projectedPoints);

    return points.map((point, actualIndex) => {
        const storedDirection = extractStoredDirection(point);
        const storedMode = normalizeDirectionMode(point?.directionMode || point?.params?.pathDirectionMode);
        const isPatternPoint = Boolean(point.patternPointId || point.source === 'pattern');

        let direction: XYPoint | null = null;
        let directionMode: PointDirectionMode | null = null;

        if (storedMode === 'manual' && storedDirection) {
            direction = storedDirection;
            directionMode = 'manual';
        } else if (isPatternPoint && storedMode !== 'auto') {
            direction = patternDirectionsByIndex.get(actualIndex) || storedDirection || deriveAutoDirection(projectedPoints, actualIndex);
            directionMode = direction ? 'pattern' : null;
        } else {
            direction = deriveAutoDirection(projectedPoints, actualIndex);
            directionMode = direction ? 'auto' : null;
        }

        return {
            ...point,
            direction,
            directionMode,
            params: buildPointParamsWithDirection(point.params, direction, directionMode),
        };
    });
};

export const resolveMissionPointDirections = <T extends MissionPointDraft | MissionPointItem>(
    points: T[]
): T[] => {
    const resolvedPoints = resolveRouteControlPointDirections(points.map(getPointCoordinates));

    return points.map((point, index) => ({
        ...point,
        params: buildPointParamsWithDirection(
            (point as MissionPointDraft | MissionPointItem).params,
            resolvedPoints[index]?.direction || null,
            resolvedPoints[index]?.directionMode || null
        ),
    }));
};

export const resolveMissionPointDirectionsFromXY = <T extends MissionPointDraft | MissionPointItem>(
    points: T[],
    getPointXY: (point: T, index: number) => XYPoint | null
): T[] => {
    const resolvedPoints = resolveLocalRouteControlPointDirections(points.map((point, index) => {
        const xy = getPointXY(point, index);
        const c = point as MissionPointDraft & MissionPointItem;
        return {
            x: xy?.x ?? null,
            y: xy?.y ?? null,
            source: c.source || (c.patternPointId ? 'pattern' : 'custom'),
            patternPointId: c.patternPointId ?? null,
            params: c.params || {},
        };
    }));

    return points.map((point, index) => ({
        ...point,
        params: buildPointParamsWithDirection(
            (point as MissionPointDraft | MissionPointItem).params,
            resolvedPoints[index]?.direction || null,
            resolvedPoints[index]?.directionMode || null
        ),
    }));
};

const buildPointRoutePlans = (
    block: ActualRouteNode[],
    routeConfig: MissionRouteConfig
): PointRoutePlan[] => {
    const plans = block.map((node, localIndex): PointRoutePlan => {
        const previousPoint = localIndex > 0 ? block[localIndex - 1].point : null;
        const nextPoint = localIndex < block.length - 1 ? block[localIndex + 1].point : null;
        const previousSegmentDirection = previousPoint
            ? normalizeVector(subtract(node.point, previousPoint))
            : null;
        const nextSegmentDirection = nextPoint
            ? normalizeVector(subtract(nextPoint, node.point))
            : null;
        const constrainedDirection = normalizeVector(node.direction || { x: 0, y: 0 });

        let backwardDirection = constrainedDirection || previousSegmentDirection;
        let forwardDirection = constrainedDirection || previousSegmentDirection;

        if (!backwardDirection && forwardDirection) {
            backwardDirection = forwardDirection;
        }

        if (!forwardDirection && backwardDirection) {
            forwardDirection = backwardDirection;
        }

        if (!forwardDirection && nextSegmentDirection) {
            forwardDirection = nextSegmentDirection;
        }

        if (!forwardDirection && backwardDirection) {
            forwardDirection = backwardDirection;
        }

        const backwardManualOffset = extractManualLeadDistance(node.params, 'pathLeadInMeters');
        const forwardManualOffset = extractManualLeadDistance(node.params, 'pathLeadOutMeters');

        return {
            backwardDirection,
            forwardDirection,
            backwardOffsetMeters: backwardManualOffset ?? 0,
            forwardOffsetMeters: forwardManualOffset ?? 0,
            backwardWarning: false,
            forwardWarning: false,
            backwardManual: backwardManualOffset !== null,
            forwardManual: forwardManualOffset !== null,
        };
    });

    if (routeConfig.routingMode === 'direct' || routeConfig.turnRadiusMeters <= 0.05) {
        return plans.map((plan) => ({
            ...plan,
            backwardOffsetMeters: 0,
            forwardOffsetMeters: 0,
            backwardWarning: false,
            forwardWarning: false,
        }));
    }

    for (let localIndex = 0; localIndex < block.length - 1; localIndex += 1) {
        const currentPlan = plans[localIndex];
        const nextPlan = plans[localIndex + 1];
        const currentForwardDirection = currentPlan.forwardDirection;
        const nextBackwardDirection = nextPlan.backwardDirection;

        if (!currentForwardDirection || !nextBackwardDirection) {
            continue;
        }

        const directionsAligned = (
            dot(currentForwardDirection, nextBackwardDirection) >= Math.cos(CORRIDOR_ALIGNMENT_TOLERANCE_RADIANS)
            && Math.abs(cross(currentForwardDirection, nextBackwardDirection)) <= Math.sin(CORRIDOR_ALIGNMENT_TOLERANCE_RADIANS)
        );

        if (directionsAligned) {
            continue;
        }

        const sharedOffsetResult = findSharedOffsetForCorridorTransition(
            block[localIndex].point,
            currentForwardDirection,
            block[localIndex + 1].point,
            scale(nextBackwardDirection, -1),
            routeConfig
        );

        if (sharedOffsetResult.warning) {
            if (!currentPlan.forwardManual) {
                currentPlan.forwardWarning = true;
            }
            if (!nextPlan.backwardManual) {
                nextPlan.backwardWarning = true;
            }
            continue;
        }

        if (sharedOffsetResult.offsetMeters >= MIN_VIRTUAL_OFFSET_METERS) {
            if (!currentPlan.forwardManual) {
                currentPlan.forwardOffsetMeters = Math.max(currentPlan.forwardOffsetMeters, sharedOffsetResult.offsetMeters);
            }
            if (!nextPlan.backwardManual) {
                nextPlan.backwardOffsetMeters = Math.max(nextPlan.backwardOffsetMeters, sharedOffsetResult.offsetMeters);
            }
        }
    }

    return plans;
};

const buildRouteNodesForBlock = (
    block: ActualRouteNode[],
    routeConfig: MissionRouteConfig
): RouteNode[] => {
    const pointPlans = buildPointRoutePlans(block, routeConfig);
    const nodes: RouteNode[] = [];

    block.forEach((node, localIndex) => {
        const pointPlan = pointPlans[localIndex];

        if (
            pointPlan.backwardDirection
            && (pointPlan.backwardOffsetMeters >= MIN_VIRTUAL_OFFSET_METERS || pointPlan.backwardWarning)
        ) {
            nodes.push({
                kind: 'virtual',
                actualIndex: null,
                point: add(node.point, scale(pointPlan.backwardDirection, -pointPlan.backwardOffsetMeters)),
                warning: pointPlan.backwardWarning,
            });
        }

        nodes.push(node);

        if (
            pointPlan.forwardDirection
            && (pointPlan.forwardOffsetMeters >= MIN_VIRTUAL_OFFSET_METERS || pointPlan.forwardWarning)
        ) {
            nodes.push({
                kind: 'virtual',
                actualIndex: null,
                point: add(node.point, scale(pointPlan.forwardDirection, pointPlan.forwardOffsetMeters)),
                warning: pointPlan.forwardWarning,
            });
        }
    });

    return nodes;
};

const buildCornerSpec = (
    previousPoint: XYPoint,
    currentPoint: XYPoint,
    nextPoint: XYPoint,
    routeConfig: MissionRouteConfig
): CornerSpec | null => {
    if (routeConfig.routingMode === 'direct' || routeConfig.turnRadiusMeters <= 0.05) {
        return null;
    }

    const incomingVector = subtract(currentPoint, previousPoint);
    const outgoingVector = subtract(nextPoint, currentPoint);
    const incomingLength = magnitude(incomingVector);
    const outgoingLength = magnitude(outgoingVector);

    if (incomingLength < EPSILON || outgoingLength < EPSILON) {
        return null;
    }

    const incomingDirection = normalizeVector(incomingVector);
    const outgoingDirection = normalizeVector(outgoingVector);
    if (!incomingDirection || !outgoingDirection) {
        return null;
    }

    const turnGeometry = getTurnGeometry(incomingDirection, outgoingDirection, routeConfig.turnRadiusMeters);
    if (!turnGeometry) {
        return null;
    }

    const maxAvailableTrimDistance = Math.min(incomingLength, outgoingLength);
    if (turnGeometry.trimDistance > maxAvailableTrimDistance + EPSILON) {
        return null;
    }

    const trimDistance = turnGeometry.trimDistance;

    if (trimDistance < 0.05) {
        return null;
    }

    const entryPoint = add(currentPoint, scale(incomingDirection, -trimDistance));
    const exitPoint = add(currentPoint, scale(outgoingDirection, trimDistance));
    const turnOrientation = turnGeometry.turnOrientation;
    const incomingNormal = scale(leftNormal(incomingDirection), turnOrientation);
    const outgoingNormal = scale(leftNormal(outgoingDirection), turnOrientation);
    const center = intersectLines(entryPoint, incomingNormal, exitPoint, outgoingNormal);

    if (!center || !Number.isFinite(center.x) || !Number.isFinite(center.y)) {
        return null;
    }

    return {
        entryPoint,
        exitPoint,
        center,
        radiusMeters: routeConfig.turnRadiusMeters,
        turnOrientation,
    };
};

const buildSegmentPolyline = (
    startPoint: XYPoint,
    endPoint: XYPoint,
    trailingCorner: CornerSpec | null,
    routeConfig: MissionRouteConfig
) => {
    const routePoints: XYPoint[] = [];
    appendPolyline(routePoints, sampleStraightSegment(startPoint, endPoint, routeConfig.sampleStepMeters));

    if (trailingCorner) {
        appendPolyline(routePoints, sampleArc(
            trailingCorner.center,
            trailingCorner.entryPoint,
            trailingCorner.exitPoint,
            trailingCorner.radiusMeters,
            trailingCorner.turnOrientation,
            routeConfig.sampleStepMeters
        ));
    }

    return {
        points: routePoints,
        routingMode: trailingCorner ? 'curve' as MissionRouteMode : 'direct' as MissionRouteMode,
    };
};

const buildValidPointBlocks = (
    points: RouteControlPoint[],
    projectedPoints: Array<XYPoint | null>
): ActualRouteNode[][] => {
    const blocks: ActualRouteNode[][] = [];
    let currentBlock: ActualRouteNode[] = [];

    projectedPoints.forEach((point, actualIndex) => {
        if (point) {
            currentBlock.push({
                kind: 'actual',
                actualIndex,
                point,
                source: points[actualIndex]?.source,
                patternPointId: points[actualIndex]?.patternPointId ?? null,
                params: points[actualIndex]?.params || null,
                direction: points[actualIndex]?.direction || null,
            });
            return;
        }

        if (currentBlock.length > 0) {
            blocks.push(currentBlock);
            currentBlock = [];
        }
    });

    if (currentBlock.length > 0) {
        blocks.push(currentBlock);
    }

    return blocks;
};

const buildRouteLegPolylines = (
    points: RouteControlPoint[],
    routeConfigValue?: Partial<MissionRouteConfig> | null
): PreviewLegPath[] => {
    const routeConfig = normalizeMissionRouteConfig(routeConfigValue);
    const resolvedPoints = resolveRouteControlPointDirections(points);
    const validPoints = resolvedPoints.filter(isValidPoint);
    if (validPoints.length < 2) {
        return [];
    }

    const projection = buildProjection(resolvedPoints);
    const projectedPoints = resolvedPoints.map((point) => (
        isValidPoint(point)
            ? projection.toXY(point)
            : null
    ));
    const blocks = buildValidPointBlocks(resolvedPoints, projectedPoints);
    const legPaths: PreviewLegPath[] = [];

    blocks.forEach((block) => {
        if (block.length < 2) {
            return;
        }

        const routeNodes = buildRouteNodesForBlock(block, routeConfig);
        const cornerSpecs = routeNodes.map((node, index) => {
            const previousNode = index > 0 ? routeNodes[index - 1] : null;
            const nextNode = index < routeNodes.length - 1 ? routeNodes[index + 1] : null;

            if (!previousNode || !nextNode || node.kind !== 'virtual') {
                return null;
            }

            return buildCornerSpec(previousNode.point, node.point, nextNode.point, routeConfig);
        });

        for (let routeIndex = 0; routeIndex < routeNodes.length - 1; routeIndex += 1) {
            const currentNode = routeNodes[routeIndex];
            const nextNode = routeNodes[routeIndex + 1];
            const startCorner = cornerSpecs[routeIndex];
            const endCorner = cornerSpecs[routeIndex + 1];
            const segmentStart = startCorner?.exitPoint || currentNode.point;
            const segmentEnd = endCorner?.entryPoint || nextNode.point;
            const segmentPolyline = buildSegmentPolyline(segmentStart, segmentEnd, endCorner, routeConfig);
            const warning = (
                (currentNode.kind === 'virtual' && (currentNode.warning || !startCorner))
                || (nextNode.kind === 'virtual' && (nextNode.warning || !endCorner))
            );

            if (segmentPolyline.points.length >= 2) {
                legPaths.push({
                    id: `preview-path-${block[0].actualIndex}-${routeIndex}`,
                    coordinates: segmentPolyline.points.map(projection.fromXY),
                    warning,
                });
            }
        }
    });

    return legPaths;
};

const getPointCoordinates = (point: MissionPointDraft | MissionPointItem): RouteControlPoint => {
    const candidate = point as MissionPointItem & MissionPointDraft;
    return {
        lat: candidate.resolvedLocation?.lat ?? candidate.lat ?? null,
        lng: candidate.resolvedLocation?.lng ?? candidate.lng ?? null,
        source: candidate.source || (candidate.patternPointId ? 'pattern' : 'custom'),
        patternPointId: candidate.patternPointId ?? null,
        params: candidate.params || {},
    };
};

export const buildMissionPreviewPaths = (
    points: Array<MissionPointDraft | MissionPointItem>,
    routeConfigValue?: Partial<MissionRouteConfig> | null
): MapPath[] => (
    buildRouteLegPolylines(
        resolveMissionPointDirections(points).map(getPointCoordinates),
        routeConfigValue
    ).map((polyline) => ({
        id: polyline.id,
        selected: true,
        coordinates: polyline.coordinates,
        warning: polyline.warning,
    }))
);
